import { StepElecContractFormData } from "../components/FormContract/types/FormContract";
import {
  StepConfirmationFormDataAPI,
  StepContractFormDataAPI,
  StepInstallationFormDataAPI,
  StepPaymentFormDataAPI,
  StepPersonFormDataAPI,
  OwnerCompany, OwnerPerson,
  StepGasContractFormDataAPI,
  StepsFormDataAPI,
  ContractationConfigResponse,
  PreConfigDataAPI
} from "../types/stepsAPI";

import {
  StepConfirmationFormData,
  StepGasContractFormData,
  StepInstallationFormData,
  StepPaymentFormData,
  StepPersonFormData,
  StepDualGasContractFormData
} from "../types/stepsFormData";

export const collectPreConfigData = (
  data: ContractationConfigResponse,
  cups: string
): PreConfigDataAPI => {
  return {
    FormConfirm: {
      cups: {
        name: cups,
      },
      owner: {
        // this is straight up wrong but @lcbaustista requested it
        // the recieved data can be a company and here is always treated 
        // as a person
        type: "F", 
        fisic_vat: data.vat,
        fisic_vat_tokenized: data.vatTokenized,
        first_name: data.name,
        surname1: data.surName1,
        surname2: data.surName2
      },
      owner_address: {
        email: data.email,
        mobile: data.mobile
      },
      step: 1
    }
  }
}

export const collectInstallationFormData = (
  data: StepInstallationFormData
): StepInstallationFormDataAPI => {
  return {
    FormConfirm: {
      allow_contact: data.leadsConsent,
      activeContract: data.activeContract,
      cups: {
        name: data.cups,
        state: data?.address?.state,
        municipi: data.address.ine,
        poblacio: data?.address.poblacio,
        tv: data?.address?.tv,
        nv: data?.address?.nv,
        pnp: data?.address?.pnp,
        bq: data?.address?.bq,
        es: data?.address?.es,
        pt: data?.address?.pt,
        pu: data?.address?.pu,
        dp: data?.address?.dp,
        aclarador: data?.address?.aclarador,
        ref_catastral: data.address.ref_catastral,
      },
      cnae: {
        cnae: data?.cnae?.name,
        vivienda: data.residenceType,
      },
      comerOrigin: data.comerOrigin || undefined,
      invoice: data.invoice || [],
      step: 1
    }
  }
}

export const collectPersonFormData = (
  data: StepPersonFormData,
  cups: string,
  activeContract: StepInstallationFormData["activeContract"]
): StepPersonFormDataAPI => {
  const owner = data.company ?
    {
      type: "J",
      name: data.name,
      juridic_vat: "ES" + data.vat,
      juridic_vat_tokenized: data.vatTokenized,
      rep_name: data.nameRepresentante,
      rep_surname1: data.surName1Representante,
      rep_surname2: data.surName2Representante,
      rep_vat: data.vatRepresentante,
      rep_name_full: `${data.surName1Representante || ""} ${data.surName2Representante || ""} , ${data.nameRepresentante || ""}`
    } as OwnerCompany
    :
    {
      type: "F",
      fisic_vat: data.vat,
      fisic_vat_tokenized: data.vatTokenized,
      first_name: data.name,
      surname1: data.surName1,
      surname2: data.surName2
    } as OwnerPerson;

  if (data.changeOwner !== null) {
    owner.changeOwner = data.changeOwner;
  }

  return {
    FormConfirm: {
      cups: {
        name: cups
      },
      owner,
      owner_address: {
        different_address: !data.sameInvoiceAddress,
        state: data?.ownerAddress?.state,
        municipi: data?.ownerAddress?.ine,
        poblacio: data?.ownerAddress?.poblacio,
        tv: data?.ownerAddress?.tv,
        nv: data?.ownerAddress?.nv,
        pnp: data?.ownerAddress?.pnp,
        bq: data?.ownerAddress?.bq,
        es: data?.ownerAddress?.es,
        pt: data?.ownerAddress?.pt,
        pu: data?.ownerAddress?.pu,
        zip: data?.ownerAddress?.dp,
        aclarador: data?.ownerAddress?.aclarador,
        mobile: data.mobile,
        email: data.email,
        ref_catastral: data.ownerAddress.ref_catastral
      },
      ...(data.company && { cif: data.nifEmpresa[0] }),
      activeContract,
      commsLanguage: data.commsLanguage,
      nif_faceup: data.nifFaceup[0],
      ...(data.nifFaceup?.[1] && { nif_facedown: data.nifFaceup[1] }),
      step: 2
    }
  }
}

export const collectContractFormData = (
  data: NonNullable<StepElecContractFormData>,
  cups: string,
  activeContract: NonNullable<StepInstallationFormData["activeContract"]>,
  changeOwner: NonNullable<StepPersonFormData["changeOwner"]>
): StepContractFormDataAPI => {

  return {
    FormConfirm: {
      activeContract,
      changeOwner,
      cups: {
        name: cups
      },
      power: data.powers.change ? 
        data.powers.new.periods[0] : data.powers.current.periods[0],
      power2: data.powers.change ? 
        data.powers.new.periods[1] : data.powers.current.periods[1],
      ...(data.powers.change ? 
        data.powers.new.installationType === "industrial" && {
          power3: data.powers.new.periods[2],
          power4: data.powers.new.periods[3],
          power5: data.powers.new.periods[4],
          power6: data.powers.new.periods[5],
        }
      : 
        data.powers.current.installationType === "industrial" && {
          power3: data.powers.current.periods[2],
          power4: data.powers.current.periods[3],
          power5: data.powers.current.periods[4],
          power6: data.powers.current.periods[5]
        }
      ),
      powerChange: data.powers.change,
      promotional_code: data.promotionalCode,
      tarifa: data.product.id,
      tarifaATR: data.accessTariff.id,
      ...(data.selfcons.enabled ? {
        selfconsCode: data.selfcons.code,
        selfcons_attach: data.selfcons.attachments
      } : {
        selfconsCode: '00',
        selfcons_attach: []
      }),
      step: 3
    }
  }
}

export const collectGasContractFormData = (
  data: StepGasContractFormData,
  cups: string
): StepGasContractFormDataAPI => {

  return {
    FormConfirm: {
      cups: {
        name: cups
      },
      tarifa: data.selectedProduct,
      tarifaATR: data.accessTariff.id,
      step: 3
    }
  }
}

export const collectDualGasContractFormData = (
  data: Extract<StepDualGasContractFormData, { includeGas: true }>,
  electricityLeadId: number
): StepGasContractFormDataAPI => {

  return {
    FormConfirm: {
      cups: {
        name: data.cups
      },
      invoice: data.invoice,
      tarifa: data.selectedProduct,
      tarifaATR: data.accessTariff.id,
      electricityLeadId,
      step: 3
    }
  }
}

export const collectPaymentFormData = (
  data: StepPaymentFormData,
  cups: string
): StepPaymentFormDataAPI => {

  return {
    FormConfirm: {
      cups: {
        name: cups
      },
      bank: {
        iban: data.iban,
        owner_is_bank_owner: data.isOwner
      },
      step: 4
    }
  }
}

export const collectConfirmationFormData = (
  data: StepConfirmationFormData,
  cups: string
): StepConfirmationFormDataAPI => {

  return {
    FormConfirm: {
      cups: {
        name: cups
      },
      accept_gdpr: data.acceptGdpr || false,
      accept_ov_conditions: data.acceptOVConditions || false,
      activate_publicity: data.acceptPublicity || false,
      confirm: true,
      ip: data.ip,
      step: 5
    }
  }
}

export const collectElecFormData = (
  stepsData: {
    installation: StepInstallationFormData,
    person: StepPersonFormData,
    contract: StepElecContractFormData,
    payment: StepPaymentFormData,
  }
): StepsFormDataAPI => {

  return {
    FormConfirm: {
      ...collectPersonFormData(
        stepsData.person, 
        stepsData.installation.cups,
        stepsData.installation.activeContract
      ).FormConfirm,
      ...collectContractFormData(
        stepsData.contract, 
        stepsData.installation.cups,
        stepsData.installation.activeContract ?? false,
        stepsData.person.changeOwner ?? false
      ).FormConfirm,
      ...collectPaymentFormData(stepsData.payment, stepsData.installation.cups).FormConfirm,

      // Important: This step must be the last to mantain the cups structure
      ...collectInstallationFormData(stepsData.installation).FormConfirm,
      step: 5
    }
  }
}

export const collectDualGasFormData = (
  stepsData: {
    installation: StepInstallationFormData,
    person: StepPersonFormData,
    dualGasContract: Extract<StepDualGasContractFormData, { includeGas: true }>,
    payment: StepPaymentFormData,
  },
  electricityLeadId: number
): StepsFormDataAPI => {

  return {
    FormConfirm: {
      ...collectPersonFormData(
        stepsData.person, 
        stepsData.dualGasContract.cups,
        stepsData.installation.activeContract
      ).FormConfirm,
      ...collectDualGasContractFormData(stepsData.dualGasContract, electricityLeadId).FormConfirm,
      ...collectPaymentFormData(stepsData.payment, stepsData.dualGasContract.cups).FormConfirm,

      // Important: This step must be the last to mantain the cups structure
      ...collectInstallationFormData({
        ...stepsData.installation,
        cups: stepsData.dualGasContract.cups,
        comerOrigin: stepsData.dualGasContract.comerOrigin
      }).FormConfirm,
      step: 5
    }
  }
}

export const collectGasFormData = (
  stepsData: {
    installation: StepInstallationFormData,
    person: StepPersonFormData,
    gasContract: StepGasContractFormData,
    payment: StepPaymentFormData,
  }
): StepsFormDataAPI => {

  return {
    FormConfirm: {
      ...collectPersonFormData(
        stepsData.person, 
        stepsData.installation.cups,
        stepsData.installation.activeContract
      ).FormConfirm,
      ...collectGasContractFormData(stepsData.gasContract, stepsData.installation.cups).FormConfirm,
      ...collectPaymentFormData(stepsData.payment, stepsData.installation.cups).FormConfirm,

      // Important: This step must be the last to mantain the cups address structure
      ...collectInstallationFormData(stepsData.installation).FormConfirm,
      step: 5
    }
  }
}
